import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  TextField,
  RadioGroup,
  Radio,
  FormControl,
  Tabs,
  Tab,
  Stack,
  Switch,
} from '@mui/material';
import {
  Save as SaveIcon,
  PointOfSale as PointOfSaleIcon,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import PageTitle from '../../components/PageTitle';
import companySettingsService from '../../services/companySettingsService';
import { useSnackbar } from 'notistack';

const PAGE_KEY_POS_SALES_KEYBOARD = 'POS_SALES_KEYBOARD';
const PAGE_BG = '#f4f6f8';
const PANEL_BORDER = '1px solid #e5e7eb';

/** Data table column visibility for POS Sales Keyboard. */
const FIELD_VISIBILITY_OPTIONS = [
  { key: 'mrp', label: 'Show MRP column' },
  { key: 'gst', label: 'Show GST (%) column' },
  { key: 'uom', label: 'Show UOM column' },
  { key: 'discount', label: 'Show Discount column' },
  { key: 'showSaleRate', label: 'Show Sale Rate (Exclusive)' },
  { key: 'showSaleRateInclusive', label: 'Show Sale Rate (Inclusive of GST)' },
  { key: 'showItemHistory', label: 'Show Item History (Sale)' },
  { key: 'showPurchaseItemHistory', label: 'Show Item History (Purchase)' },
  { key: 'showPurchaseRate', label: 'Show Purchase Rate' },
  { key: 'enableLineItemNotes', label: 'POS: per-line item notes (IMEI, warranty, etc.)' },
];

/** Focus/tab order fields for POS Sales Keyboard. Order number (1, 2, 3, …) or empty to skip. */
const FOCUS_ORDER_FIELDS = [
  { key: 'itemCode', label: 'Item Code' },
  { key: 'qty', label: 'Qty' },
  { key: 'uom', label: 'UOM' },
  { key: 'saleRate', label: 'Sale Rate' },
  { key: 'saleRateInclusive', label: 'Sale Rate (Inclusive)' },
  { key: 'discount', label: 'Discount (% / ₹)' },
];

/** Initial focus selection for POS Sales Keyboard. */
const INITIAL_FOCUS_OPTIONS = [
  { key: 'CUSTOMER', label: 'Customer field' },
  { key: 'BARCODE', label: 'Barcode / Item Code' },
];

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`pos-settings-tabpanel-${index}`}>
      {value === index ? children : null}
    </div>
  );
}

function SettingSwitchRow({ label, checked, onChange }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        borderBottom: PANEL_BORDER,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, pr: 2 }}>
        {label}
      </Typography>
      <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} color="primary" />
    </Box>
  );
}

const PageFieldSettingsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await companySettingsService.getPageFieldConfig(PAGE_KEY_POS_SALES_KEYBOARD);
      const safe = data && typeof data === 'object' ? data : {};
      if (safe.roundOffEnabled === undefined) {
        safe.roundOffEnabled = false;
      }
      if (!safe.roundOffMode) {
        safe.roundOffMode = 'NEAREST';
      }
      if (!safe.initialFocus) {
        safe.initialFocus = 'BARCODE';
      }
      if (safe.showItemHistory === undefined) {
        safe.showItemHistory = true;
      }
      if (safe.showPurchaseItemHistory === undefined) {
        safe.showPurchaseItemHistory = true;
      }
      if (safe.showSaleRate === undefined) {
        safe.showSaleRate = true;
      }
      if (safe.showSaleRateInclusive === undefined) {
        safe.showSaleRateInclusive = false;
      }
      if (safe.showPurchaseRate === undefined) {
        safe.showPurchaseRate = false;
      }
      if (safe.allowDecimalQty === undefined) {
        safe.allowDecimalQty = false;
      }
      if (safe.enableLineItemNotes === undefined) {
        safe.enableLineItemNotes = false;
      }
      if (safe.enableAddEditInPos === undefined) {
        safe.enableAddEditInPos = false;
      }
      setConfig(safe);
    } catch {
      setConfig({});
    } finally {
      setLoading(false);
    }
  };

  const getFieldValue = (fieldKey) => config[fieldKey] !== false;

  const setFieldValue = (fieldKey, value) => {
    setConfig((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const getFocusOrderValue = (fieldKey) => {
    const focusOrder = config?.focusOrder;
    if (!focusOrder || typeof focusOrder !== 'object') return '';
    const v = focusOrder[fieldKey];
    if (v == null || v === '') return '';
    const n = Number(v);
    return Number.isInteger(n) && n >= 1 ? String(n) : '';
  };

  const setFocusOrderValue = (fieldKey, value) => {
    setConfig((prev) => {
      const currentFocus = prev.focusOrder && typeof prev.focusOrder === 'object' ? { ...prev.focusOrder } : {};
      if (value === '' || value == null) {
        delete currentFocus[fieldKey];
      } else {
        const n = parseInt(value, 10);
        if (Number.isInteger(n) && n >= 1) currentFocus[fieldKey] = n;
      }
      return { ...prev, focusOrder: currentFocus };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companySettingsService.updatePageFieldConfig(PAGE_KEY_POS_SALES_KEYBOARD, config);
      enqueueSnackbar('POS settings saved successfully.', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to save POS settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const actionBar = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
      <Button
        variant="contained"
        size="small"
        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving || loading}
      >
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </Stack>
  );

  if (loading) {
    return (
      <Layout>
        <PageTitle title="POS Settings" />
        <Box sx={{ bgcolor: PAGE_BG, minHeight: '70vh' }}>
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
              <CircularProgress />
            </Box>
          </Container>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageTitle title="POS Settings" />
      <Box sx={{ bgcolor: PAGE_BG, pb: 4 }}>
        <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              border: PANEL_BORDER,
              borderRadius: 2.5,
              overflow: 'hidden',
              bgcolor: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                borderBottom: PANEL_BORDER,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PointOfSaleIcon sx={{ fontSize: 22, color: '#fff' }} />
              </Box>
              <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                <Typography variant="h6" component="h1" sx={{ fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.3 }}>
                  POS settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  Configure columns, keyboard flow, billing, and stock rules for the sales screen.
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0 }}>{actionBar}</Box>
            </Box>

            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="POS settings sections"
              sx={{
                px: { xs: 1, sm: 2 },
                borderBottom: PANEL_BORDER,
                minHeight: 48,
                '& .MuiTab-root': {
                  minHeight: 48,
                  py: 1.25,
                  px: 2,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  color: 'text.secondary',
                  '&.Mui-selected': { color: 'primary.main', fontWeight: 600 },
                },
                '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
              }}
            >
              <Tab label="Display fields" id="pos-settings-tab-0" aria-controls="pos-settings-tabpanel-0" />
              <Tab label="Input & navigation" id="pos-settings-tab-1" aria-controls="pos-settings-tabpanel-1" />
              <Tab label="Billing & rounding" id="pos-settings-tab-2" aria-controls="pos-settings-tabpanel-2" />
              <Tab label="Inventory logic" id="pos-settings-tab-3" aria-controls="pos-settings-tabpanel-3" />
            </Tabs>

            <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
              <TabPanel value={tabValue} index={0}>
                <Paper elevation={0} sx={{ border: PANEL_BORDER, borderRadius: 2, px: 2, bgcolor: '#fafbfc' }}>
                  {FIELD_VISIBILITY_OPTIONS.map((field) => (
                    <SettingSwitchRow
                      key={field.key}
                      label={field.label}
                      checked={getFieldValue(field.key)}
                      onChange={(value) => setFieldValue(field.key, value)}
                    />
                  ))}
                </Paper>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Where the cursor lands when the POS screen opens.
                    </Typography>
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        value={config.initialFocus || 'BARCODE'}
                        onChange={(e) => setFieldValue('initialFocus', e.target.value)}
                      >
                        <Stack spacing={1}>
                          {INITIAL_FOCUS_OPTIONS.map((opt) => (
                            <Paper
                              key={opt.key}
                              elevation={0}
                              sx={{
                                p: 1.25,
                                border: config.initialFocus === opt.key ? '2px solid' : '1px solid',
                                borderColor: config.initialFocus === opt.key ? 'primary.main' : '#e2e8f0',
                                borderRadius: 1.5,
                                cursor: 'pointer',
                                bgcolor: config.initialFocus === opt.key ? 'rgba(25, 118, 210, 0.06)' : '#fff',
                                '&:hover': { borderColor: 'primary.light', bgcolor: '#f8fafc' },
                              }}
                              onClick={() => setFieldValue('initialFocus', opt.key)}
                            >
                              <FormControlLabel
                                value={opt.key}
                                control={<Radio size="small" />}
                                label={
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {opt.label}
                                  </Typography>
                                }
                                sx={{ m: 0, width: '100%' }}
                              />
                            </Paper>
                          ))}
                        </Stack>
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Tab order for keyboard navigation. Enter 1, 2, 3… or leave blank to skip a field.
                    </Typography>
                    <Grid container spacing={2}>
                      {FOCUS_ORDER_FIELDS.map((f) => (
                        <Grid item xs={6} sm={4} md={3} key={f.key}>
                          <TextField
                            size="small"
                            label={f.label}
                            type="number"
                            value={getFocusOrderValue(f.key)}
                            onChange={(e) => setFocusOrderValue(f.key, e.target.value)}
                            placeholder="Skip"
                            fullWidth
                            inputProps={{ min: 1, max: 10, step: 1 }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  <Paper elevation={0} sx={{ border: PANEL_BORDER, borderRadius: 2, px: 2, bgcolor: '#fafbfc' }}>
                    <SettingSwitchRow
                      label="Allow decimal values in Qty column (e.g. 1.5)"
                      checked={Boolean(config.allowDecimalQty)}
                      onChange={(value) => setFieldValue('allowDecimalQty', value)}
                    />
                    <SettingSwitchRow
                      label="Enable add and edit product on POS screen (double-click row to edit; open product form when not found)"
                      checked={Boolean(config.enableAddEditInPos)}
                      onChange={(value) => setFieldValue('enableAddEditInPos', value)}
                    />
                  </Paper>
                </Stack>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Stack spacing={2.5}>
                  <Paper elevation={0} sx={{ border: PANEL_BORDER, borderRadius: 2, px: 2, bgcolor: '#fafbfc' }}>
                    <SettingSwitchRow
                      label="Auto-apply last sale price for customer"
                      checked={Boolean(config.autoApplyPreviousPrice)}
                      onChange={(value) => setFieldValue('autoApplyPreviousPrice', value)}
                    />
                  </Paper>

                  <Box>
                    <Paper elevation={0} sx={{ border: PANEL_BORDER, borderRadius: 2, px: 2, bgcolor: '#fafbfc', mb: config.roundOffEnabled ? 2 : 0 }}>
                      <SettingSwitchRow
                        label="Enable round off"
                        checked={Boolean(config.roundOffEnabled)}
                        onChange={(value) => setFieldValue('roundOffEnabled', value)}
                      />
                    </Paper>

                    {config.roundOffEnabled && (
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          value={config.roundOffMode || 'NEAREST'}
                          onChange={(e) => setFieldValue('roundOffMode', e.target.value)}
                        >
                          <Stack spacing={1}>
                            {[
                              { val: 'DOWN', label: 'Down', desc: '11.89 → 11' },
                              { val: 'UP', label: 'Up', desc: '11.01 → 12' },
                              { val: 'NEAREST', label: 'Nearest', desc: '(.49 ↓, .50 ↑)' },
                            ].map((item) => (
                              <Paper
                                key={item.val}
                                elevation={0}
                                sx={{
                                  p: 1.25,
                                  border: config.roundOffMode === item.val ? '2px solid' : '1px solid',
                                  borderColor: config.roundOffMode === item.val ? 'primary.main' : '#e2e8f0',
                                  borderRadius: 1.5,
                                  cursor: 'pointer',
                                  bgcolor: config.roundOffMode === item.val ? 'rgba(25, 118, 210, 0.06)' : '#fff',
                                  '&:hover': { borderColor: 'primary.light', bgcolor: '#f8fafc' },
                                }}
                                onClick={() => setFieldValue('roundOffMode', item.val)}
                              >
                                <FormControlLabel
                                  value={item.val}
                                  control={<Radio size="small" />}
                                  label={
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {item.label}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {item.desc}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: 'flex-start' }}
                                />
                              </Paper>
                            ))}
                          </Stack>
                        </RadioGroup>
                      </FormControl>
                    )}
                  </Box>
                </Stack>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <Paper elevation={0} sx={{ border: PANEL_BORDER, borderRadius: 2, px: 2, bgcolor: '#fafbfc' }}>
                  <SettingSwitchRow
                    label="Allow sales when stock is zero or negative"
                    checked={Boolean(config.allowNegativeStock)}
                    onChange={(value) => setFieldValue('allowNegativeStock', value)}
                  />
                </Paper>
              </TabPanel>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Layout>
  );
};

export default PageFieldSettingsPage;
